"use client";

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Asset } from "./types";
import { useOspApiClientProvider } from "@/src/hooks";

const DEFAULT_STOCK_IDS = ["AAPL", "NVDA", "TSLA", "GOOG"] as const;
const DEFAULT_CRYPTO_IDS = ["BTC", "ETH", "SOL", "BNB"] as const;

const buildCryptoAssets = (rows: any[] = []): Asset[] => {
  const tokensMap = new Map<string, any>();
  rows.forEach((row) => {
    const key = row?.symbol?.toUpperCase();
    if (key) {
      tokensMap.set(key, row);
    }
  });

  return DEFAULT_CRYPTO_IDS.map((symbol) => {
    const tokenInfo = tokensMap.get(symbol.toUpperCase());
    if (!tokenInfo) {
      return null;
    }
    // 获取 24h 涨跌幅
    const change24h = tokenInfo?.market_data?.price_change_percentage?.find(
      (item: any) => item.time_period === "24h",
    );
    const changePercent = change24h ? Number(change24h.percentage) : undefined;

    return {
      id: tokenInfo?.id ?? symbol,
      ticker: tokenInfo?.symbol ?? symbol,
      name: tokenInfo?.name ?? symbol,
      iconUrl: tokenInfo?.logo ?? "",
      changePercent,
    };
  }).filter(Boolean) as Asset[];
};

const buildStockAssets = (rows: any[] = []): Asset[] => {
  const stocksMap = new Map<string, any>();
  rows.forEach((row) => {
    const key = row?.symbol?.toUpperCase() || row?.ticker?.toUpperCase();
    if (key) {
      stocksMap.set(key, row);
    }
  });

  return DEFAULT_STOCK_IDS.map((symbol) => {
    const record = stocksMap.get(symbol.toUpperCase());
    if (!record) {
      return null;
    }
    // 计算涨跌幅：使用当前价格和前一收盘价计算
    let changePercent: number | undefined = undefined;
    const currentPrice = record?.trading_data?.current_price;
    const previousClose = record?.trading_data?.previous_close;

    if (currentPrice && previousClose) {
      const current = Number(currentPrice);
      const previous = Number(previousClose);
      if (previous > 0) {
        changePercent = ((current - previous) / previous) * 100;
      }
    }

    return {
      id: record?.id ?? symbol,
      ticker: record?.symbol ?? symbol,
      name: record?.company_name ?? symbol,
      iconUrl: record?.logo ?? "",
      changePercent,
    };
  }).filter(Boolean) as Asset[];
};

/**
 * useWatchlistAssets Hook
 * 获取推荐的资产列表（用于 watchlist 空状态）
 *
 * @returns 推荐的资产列表和加载状态
 */
export const useWatchlistAssets = () => {
  const { openDataApi } = useOspApiClientProvider();

  const cryptoQuery = useQuery({
    queryKey: ["watchlist-assets", "crypto", DEFAULT_CRYPTO_IDS],
    enabled: !!openDataApi,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await openDataApi?.tokens.getTokens({
        limit: DEFAULT_CRYPTO_IDS.length,
        symbols: [...DEFAULT_CRYPTO_IDS],
        offset: 0,
        "enrich.enable": true,
        "enrich.with_market.enable": true,
      });
      return res?.data?.data?.rows ?? [];
    },
    select: (rows) => buildCryptoAssets(rows),
  });

  const stocksQuery = useQuery({
    queryKey: ["watchlist-assets", "stocks", DEFAULT_STOCK_IDS],
    enabled: !!openDataApi,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await openDataApi?.stocks.getStocks({
        symbols: [...DEFAULT_STOCK_IDS],
        limit: DEFAULT_STOCK_IDS.length,
        "enrich.enable": true,
        "enrich.with_trading_data": true,
      } as any);
      return res?.data?.data?.rows ?? [];
    },
    select: (rows) => buildStockAssets(rows),
  });

  const cryptoAssets = useMemo(
    () => cryptoQuery.data ?? [],
    [cryptoQuery.data],
  );
  const stockAssets = useMemo(() => stocksQuery.data ?? [], [stocksQuery.data]);

  return {
    crypto: cryptoAssets,
    stocks: stockAssets,
    isLoading: cryptoQuery.isLoading || stocksQuery.isLoading,
  };
};
